import React, { useState, useEffect, useMemo } from 'react';
import Select from 'react-select';
import { useAuth } from '../contexts/AuthContext';
import Loader from '../components/Loader';
import Pagination from '../components/Pagination';
import StoreBatchModal from '../components/StoreBatchModal';

const API_BASE = '/api';

const Store = () => {
  const { user, token } = useAuth();
  // State for filters
  const [products, setProducts] = useState([]); // All products
  const [categories, setCategories] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [showSavingTerm, setShowSavingTerm] = useState(false);
  const [showAboveMinQty, setShowAboveMinQty] = useState(false);
  const [modalProduct, setModalProduct] = useState(null); // Product for modal
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(20);
  const [barcode, setBarcode] = useState('');
  const [highlightBarcode, setHighlightBarcode] = useState('');
  const [barcodeResultProductId, setBarcodeResultProductId] = useState(null);
  const [barcodeLoading, setBarcodeLoading] = useState(false);

  // Fetch categories and stores on mount
  useEffect(() => {
    const headers = {
      'Content-Type': 'application/json',
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
      'X-Owner-Id': String(user?.ownerId || user?.id || ''),
      'X-Staff-Id': String(user?.staffId ?? '')
    };
    fetch(`${API_BASE}/categories`, { headers })
      .then(res => res.json())
      .then(data => setCategories(data.items || []));
  }, []);

  // Reset pagination when filters change
  useEffect(() => {
    setCurrentPage(1);
  }, [showSavingTerm, showAboveMinQty, selectedCategory, searchTerm]);

  // Fetch products when filters change (server-side filters: category, name)
  useEffect(() => {
    setLoading(true);
    setError(null);
    const params = new URLSearchParams();
    if (selectedCategory) params.append('category_id', selectedCategory);
    if (searchTerm) params.append('name', searchTerm);
    const headers = {
      'Content-Type': 'application/json',
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
      'X-Owner-Id': String(user?.ownerId || user?.id || ''),
      'X-Staff-Id': String(user?.staffId ?? '')
    };
    fetch(`${API_BASE}/store-products/products?${params.toString()}`, { headers })
      .then(res => {
        if (!res.ok) throw new Error('Server error');
        return res.json();
      })
      .then(data => {
        setProducts(Array.isArray(data) ? data : []);
        setLoading(false);
      })
      .catch(err => {
        setError('Mahsulotlarni yuklashda xatolik yuz berdi');
        setLoading(false);
      });
  }, [selectedCategory, searchTerm]);

  // Derived filtered and sorted products
  const filteredProducts = useMemo(() => {
    let list = Array.isArray(products) ? [...products] : [];
    if (barcodeResultProductId) {
      list = list.filter(p => p.id === barcodeResultProductId);
    }
    if (showSavingTerm) {
      list = list.filter(p => !!p.saving_term);
      list.sort((a, b) => new Date(a.saving_term) - new Date(b.saving_term));
    }
    if (showAboveMinQty) {
      // Show all products, but sort by how far below minimum they are
      list.sort((a, b) => {
        const aRemain = Number(a.sum_remaining_quantity) || 0;
        const bRemain = Number(b.sum_remaining_quantity) || 0;
        const aMin = Number(a.minimum_quantity) || 0;
        const bMin = Number(b.minimum_quantity) || 0;
        const aDelta = aRemain - aMin; // negative means below minimum
        const bDelta = bRemain - bMin;
        return aDelta - bDelta; // most negative (least) first
      });
    }
    return list;
  }, [products, showSavingTerm, showAboveMinQty, barcodeResultProductId]);

  // Pagination calculations
  const totalItems = filteredProducts.length;
  const startIndex = (currentPage - 1) * itemsPerPage;
  const paginatedProducts = filteredProducts.slice(startIndex, startIndex + itemsPerPage);


  const handleProductClick = (product) => {
    setSelectedProduct(product);
    setHighlightBarcode('');
  };

  const handleBatchUpdate = (batchId, updatedData) => {
    // This callback can be used if we need to update the main product list
    // when a batch is updated in the modal
    console.log('Batch updated:', batchId, updatedData);
  };

  const clearBarcodeSelection = () => {
    setBarcode('');
    setHighlightBarcode('');
    setBarcodeResultProductId(null);
  };

  const handleBarcodeSearch = async () => {
    const code = String(barcode).trim();
    if (!code) return;
    setBarcodeLoading(true);

    // 1) Try direct product barcode match in current list
    let directProduct = (Array.isArray(products) ? products : []).find(p => String(p.barcode || '') === code);
      if (directProduct) {
        if (directProduct.costing_method === 'Specific') {
          setSelectedProduct(directProduct);
          setHighlightBarcode(code);
          setBarcodeResultProductId(directProduct.id);
        } else {
          // FIFO: show only this product in the table
          setSelectedProduct(null);
          setHighlightBarcode('');
          setBarcodeResultProductId(directProduct.id);
        }
        setCurrentPage(1);
        setBarcodeLoading(false);
        return;
      }

    // 2) Search in Specific products' batches (only quantity_remaining > 0)
    const specificProducts = (Array.isArray(products) ? products : []).filter(p => p.costing_method === 'Specific');
    const headers = {
      'Content-Type': 'application/json',
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
      'X-Owner-Id': String(user?.ownerId || user?.id || ''),
      'X-Staff-Id': String(user?.staffId ?? '')
    };
    for (const prod of specificProducts) {
      try {
        const res = await fetch(`${API_BASE}/store-products/batch/${prod.id}`, { headers });
        if (!res.ok) continue;
        const data = await res.json();
        const available = (Array.isArray(data) ? data : []).filter(b => Number(b.quantity_remaining) > 0);
        const matched = available.find(b => String(b.barcode || '') === code);
        if (matched) {
          setSelectedProduct(prod);
          setHighlightBarcode(code);
          setBarcodeResultProductId(prod.id);
          setCurrentPage(1);
          setBarcodeLoading(false);
          return;
        }
      } catch (e) {
        // ignore and continue
      }
    }

    // 3) Fallback: fetch all products (ignore current filters) and retry direct product match for FIFO
    try {
      const headers2 = {
        'Content-Type': 'application/json',
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
        'X-Owner-Id': String(user?.ownerId || user?.id || ''),
        'X-Staff-Id': String(user?.staffId ?? '')
      };
      const resAll = await fetch(`${API_BASE}/store-products/products`, { headers: headers2 });
      if (resAll.ok) {
        const all = await resAll.json();
        const allArr = Array.isArray(all) ? all : [];
        setProducts(allArr);
        directProduct = allArr.find(p => String(p.barcode || '') === code);
        if (directProduct) {
          if (directProduct.costing_method === 'Specific') {
            setSelectedProduct(directProduct);
            setHighlightBarcode(code);
            setBarcodeResultProductId(directProduct.id);
          } else {
            setSelectedProduct(null);
            setHighlightBarcode('');
            setBarcodeResultProductId(directProduct.id);
          }
          setCurrentPage(1);
          setBarcodeLoading(false);
          return;
        }
      }
    } catch (e) {
      // ignore
    }

    alert('Shtrix-kod topilmadi');
    setBarcodeLoading(false);
  };

  const handleBarcodeKeyDown = async (e) => {
    if (e.key === 'Enter') {
      await handleBarcodeSearch();
    }
  };

  return (
    <div className="container-fluid p-4">
      <h1 className="h2 fw-bold mb-4">Ombordagi tovarlar</h1>
      {/* Filter UI */}
      <div className="d-flex flex-wrap gap-2 mb-4">
        <button className="btn btn-outline-success btn-sm" onClick={() => { setShowSavingTerm(false); setShowAboveMinQty(false); setSelectedCategory(''); setSearchTerm(''); clearBarcodeSelection(); }}>Barcha mahsulotlar</button>
        <button className={`btn btn-sm ${showSavingTerm ? 'btn-success' : 'btn-outline-success'}`} onClick={() => { setShowSavingTerm(true); setShowAboveMinQty(false); }}>Saqlash muddati </button>
        <button className={`btn btn-sm ${showAboveMinQty ? 'btn-success' : 'btn-outline-success'}`} onClick={() => { setShowAboveMinQty(true); setShowSavingTerm(false); }}>Kam qolgan mahsulotlar</button>
        
        <div className="input-group input-group-sm" style={{width: 'auto'}}>
          <input
            className="form-control"
            placeholder="Shtrix-kod"
            value={barcode}
            onChange={e => setBarcode(e.target.value)}
            onKeyDown={handleBarcodeKeyDown}
          />
          <button 
            className="btn btn-success d-inline-flex align-items-center" 
            onClick={handleBarcodeSearch} 
            disabled={barcodeLoading}
          >
            {barcodeLoading ? (
              <span className="spinner-border spinner-border-sm me-1" role="status" aria-hidden="true"></span>
            ) : (
              <i className="bi bi-search me-1"></i>
            )}
            Qidirish
          </button>
        </div>

        <input
          className="form-control form-control-sm"
          style={{width: 'auto'}}
          placeholder="Nomi bo'yicha qidirish"
          value={searchTerm}
          onFocus={() => { setShowSavingTerm(false); setShowAboveMinQty(false); setSelectedCategory(''); }}
          onChange={e => setSearchTerm(e.target.value)}
        />
        <div style={{ minWidth: '240px' }}>
          <Select
            classNamePrefix="react-select"
            isClearable
            isSearchable
            placeholder="Barcha kategoriyalar"
            value={(() => {
              const found = categories.find(c => String(c.id) === String(selectedCategory));
              return found ? { value: String(found.id), label: found.name } : null;
            })()}
            onChange={(opt) => setSelectedCategory(opt ? opt.value : '')}
            options={categories.map(c => ({ value: String(c.id), label: c.name }))}
            styles={{
              container: (base) => ({ ...base, minWidth: 240 }),
              control: (base) => ({ ...base, minHeight: 31, height: 31 }),
              valueContainer: (base) => ({ ...base, paddingTop: 0, paddingBottom: 0 }),
              indicatorsContainer: (base) => ({ ...base, height: 31 })
            }}
          />
        </div>
      </div>
      {loading && <Loader />}
      {error && <div className="alert alert-danger py-2 mb-2">{error}</div>}
      {/* Table */}
      <div className="table-responsive shadow rounded-4">
        <table className="table table-striped table-sm table-hover border">
          <thead className="">
            <tr>
              <th className="border px-2 py-1">Rasm</th>
              <th className="border px-2 py-1">Nomi</th>
              <th className="border px-2 py-1">Kategoriya</th>
              <th className="border px-2 py-1">Qoldiq (birlik)</th>
              <th className="border px-2 py-1">Mavjud partiyalar</th>
              <th className="border px-2 py-1">Oxirgi partiya: Narxi</th>
              <th className="border px-2 py-1">Oxirgi partiya: Birinchi narx</th>
              <th className="border px-2 py-1">Oxirgi partiya: Sotish narxi</th>
              <th className="border px-2 py-1">Hisoblash usuli</th>
              <th className="border px-2 py-1">Saqlash muddati</th>
              <th className="border px-2 py-1">View Batches</th>
            </tr>
          </thead>
          <tbody>
            {paginatedProducts.map(product => (
              <tr key={product.id} className="hover:bg-gray-100 cursor-pointer">
                <td className="border px-2 py-1"><img src={product.photo_url} alt="rasm" className="w-12 h-12 object-cover rounded" style={{width: '48px', height: '48px'}} /></td>
                <td className="border px-2 py-1">{product.name}</td>
                <td className="border px-2 py-1">{product.category_name}</td>
                <td className="border px-2 py-1">{product.sum_remaining_quantity} {product.birlik_name}</td>
                <td className="border px-2 py-1">{product.available_batches_count ?? (product.available_batches?.length || 0)}</td>
                <td className="border px-2 py-1">{product.last_batch?.cost_per_unit} {product.last_batch?.currency}</td>
                <td className="border px-2 py-1">{product.last_batch?.first_price} {product.last_batch?.currency}</td>
                <td className="border px-2 py-1">{product.last_batch?.selling_price} {product.last_batch?.currency}</td>
                <td className="border px-2 py-1">{product.costing_method}</td>
                <td className="border px-2 py-1">{product.saving_term || '-'}</td>
                <td className="border px-2 py-1 ">
                  <div className="d-flex justify-content-center">
                    <button className="btn btn-sm btn-success" onClick={() => handleProductClick(product)}>Partiyalar</button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <Pagination
        currentPage={currentPage}
        totalItems={totalItems}
        itemsPerPage={itemsPerPage}
        onPageChange={setCurrentPage}
        onItemsPerPageChange={(n) => { setItemsPerPage(n); setCurrentPage(1); }}
      />
      {/* Bootstrap Modal for batch details */}
      <div className="modal" tabIndex="-1" role="dialog" style={{ display: modalProduct ? 'block' : 'none', zIndex: 1060 }}>
        <div className="modal-dialog modal-dialog-centered">
          <div className="modal-content">
            <div className="modal-header">
              <h5 className="modal-title">{modalProduct?.name} - Partiyalar</h5>
              <button type="button" className="close" aria-label="Close" onClick={() => setModalProduct(null)}>
                <span aria-hidden="true">&times;</span>
              </button>
            </div>
            <div className="modal-body">
              {modalProduct?.available_batches?.filter(b => b.quantity_purchased > 0).map((batch, idx) => (
                <div key={batch.id || batch.ID || idx} className="border-bottom py-2 mb-2">
                  <div><strong>Partiya ID:</strong> {batch.id || batch.ID}</div>
                  <div><strong>Soni:</strong> {batch.quantity_purchased}</div>
                  <div><strong>Tan narxi:</strong> {batch.cost_per_unit} {batch.currency}</div>
                  <div><strong>Sotish narxi:</strong> {batch.selling_price} {batch.currency}</div>
                </div>
              ))}
            </div>
            <div className="modal-footer">
              <button type="button" className="btn btn-secondary" onClick={() => setModalProduct(null)}>Close</button>
            </div>
          </div>
        </div>
      </div>

      {/* Purchase Batches Modal */}
      <StoreBatchModal
        show={!!selectedProduct}
        onClose={() => { setSelectedProduct(null); setHighlightBarcode(''); }}
        selectedProduct={selectedProduct}
        onBatchUpdate={handleBatchUpdate}
      />
    </div>
  );
};

export default Store;